import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { authAPI } from '../services/api';

const APIPage: React.FC = () => {
  const { user } = useAuth();
  const [copied, setCopied] = useState('');
  const [batchId, setBatchId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [batchResult, setBatchResult] = useState<{ success: boolean; message: string; details?: any } | null>(null);

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const processBatch = async () => {
    if (!batchId.trim()) {
      setBatchResult({ success: false, message: 'Please enter a batch ID' });
      return;
    }

    setProcessing(true);
    setBatchResult(null);

    try {
      const result = await authAPI.processBatch(batchId.trim());
      setBatchResult(result);
      if (result.success) {
        setBatchId('');
      }
    } catch (error) {
      setBatchResult({ 
        success: false, 
        message: error instanceof Error ? error.message : 'Failed to process batch' 
      });
    } finally {
      setProcessing(false);
    }
  };

  const downloadLocationCodes = () => {
    const apiUrl = process.env.NODE_ENV === 'production'
      ? 'https://netloxproxies.com'
      : 'http://localhost:3101';
    window.open(`${apiUrl}/api/locations/download-csv`, '_blank');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-gray-700 border-t-blue-500 rounded-full animate-spin"></div>
            <div className="absolute inset-2 bg-blue-500/20 rounded-full animate-pulse"></div>
            <div className="absolute inset-6 bg-blue-500 rounded-full"></div>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Loading</h2>
          <p className="text-sm text-gray-400">Please wait...</p>
        </div>
      </div>
    );
  }

  const exampleUsername = `${user.proxyUserId}_US-3581-000993_10080_ABC123XY`;
  const exampleProxy = `proxy.netloxproxies.com:8080:${exampleUsername}:${user.proxyPassword}`;

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">Settings</h1>
            <p className="text-sm text-gray-400">
              Learn how to generate and use proxies
            </p>
          </div>
          <a
            href="https://t.me/NetloxProxies"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-all text-sm whitespace-nowrap"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
            </svg>
            Help
          </a>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Main Section */}
          <div className="lg:col-span-2 space-y-4">
            {/* How It Works */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <h2 className="text-base md:text-lg font-semibold text-white mb-3">How Netlox Proxies Work</h2>
              <p className="text-gray-400 text-sm mb-4">
                Netlox uses a username-based system. You generate proxies by creating custom usernames that specify location, duration, and session.
              </p>
              
              <div className="space-y-4">
                {/* Username Format */}
                <div className="bg-gray-900 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-white mb-2">Username Format</h3>
                  <div className="font-mono text-xs text-green-400 mb-3 break-all">
                    {user.proxyUserId}_LOCATION_DURATION_SESSION
                  </div>
                  
                  <div className="space-y-2 text-xs">
                    <div className="flex items-start gap-2">
                      <span className="text-blue-400 font-mono min-w-[100px]">{user.proxyUserId}</span>
                      <span className="text-gray-400">Your unique Proxy User ID (fixed)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-purple-400 font-mono min-w-[100px]">LOCATION</span>
                      <span className="text-gray-400">ISO country code + optional state + city codes</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-yellow-400 font-mono min-w-[100px]">DURATION</span>
                      <span className="text-gray-400">Sticky session time in minutes (10-10080)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-pink-400 font-mono min-w-[100px]">SESSION</span>
                      <span className="text-gray-400">Random string for unique sessions</span>
                    </div>
                  </div>
                </div>

                {/* Location Format */}
                <div className="bg-gray-900 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-white mb-2">Location Format</h3>
                  <div className="space-y-2 text-xs">
                    <div>
                      <div className="text-gray-400 mb-1">Country Only:</div>
                      <code className="text-green-400">US</code>
                    </div>
                    <div>
                      <div className="text-gray-400 mb-1">Country + State:</div>
                      <code className="text-green-400">US-3581</code>
                      <span className="text-gray-500 ml-2">(US, New York State)</span>
                    </div>
                    <div>
                      <div className="text-gray-400 mb-1">Country + State + City:</div>
                      <code className="text-green-400">US-3581-000993</code>
                      <span className="text-gray-500 ml-2">(US, New York State, New York City)</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={downloadLocationCodes}
                    className="mt-3 w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-medium transition-all flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download All Location Codes (CSV)
                  </button>
                </div>

                {/* Duration Examples */}
                <div className="bg-gray-900 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-white mb-2">Duration Examples</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <code className="text-yellow-400">10</code>
                      <span className="text-gray-400 ml-2">= 10 minutes</span>
                    </div>
                    <div>
                      <code className="text-yellow-400">60</code>
                      <span className="text-gray-400 ml-2">= 1 hour</span>
                    </div>
                    <div>
                      <code className="text-yellow-400">1440</code>
                      <span className="text-gray-400 ml-2">= 24 hours</span>
                    </div>
                    <div>
                      <code className="text-yellow-400">10080</code>
                      <span className="text-gray-400 ml-2">= 7 days (max)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Your Credentials */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <h2 className="text-base md:text-lg font-semibold text-white mb-3">Your Proxy Credentials</h2>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Proxy User ID</label>
                  <div className="bg-gray-900 rounded-lg p-3 flex items-center gap-3">
                    <code className="text-green-400 font-mono text-sm flex-1">{user.proxyUserId}</code>
                    <button
                      onClick={() => copyToClipboard(user.proxyUserId, 'userid')}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                        copied === 'userid' ? 'bg-green-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      }`}
                    >
                      {copied === 'userid' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Proxy Password</label>
                  <div className="bg-gray-900 rounded-lg p-3 flex items-center gap-3">
                    <code className="text-green-400 font-mono text-sm flex-1">{user.proxyPassword}</code>
                    <button
                      onClick={() => copyToClipboard(user.proxyPassword, 'password')}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                        copied === 'password' ? 'bg-green-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      }`}
                    >
                      {copied === 'password' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Example Proxy</label>
                  <div className="bg-gray-900 rounded-lg p-3 flex items-center gap-3">
                    <code className="text-green-400 font-mono text-xs break-all flex-1">{exampleProxy}</code>
                    <button
                      onClick={() => copyToClipboard(exampleProxy, 'example')}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-all whitespace-nowrap ${
                        copied === 'example' ? 'bg-green-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      }`}
                    >
                      {copied === 'example' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">New York City, 7-day sticky session</p>
                </div>
              </div>
            </div>

            {/* Batch Processing */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <h2 className="text-base md:text-lg font-semibold text-white mb-3">Associate Batch Proxies</h2>
              <p className="text-gray-400 text-xs mb-3">
                Link your batch proxies to your account for easy management
              </p>
              
              <div className="space-y-3">
                <div>
                  <label htmlFor="batchId" className="block text-sm font-medium text-gray-300 mb-2">
                    Batch ID
                  </label>
                  <input
                    type="text"
                    id="batchId"
                    value={batchId}
                    onChange={(e) => setBatchId(e.target.value)}
                    placeholder="Enter your batch ID"
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 text-sm"
                    disabled={processing}
                  />
                </div>

                <button
                  onClick={processBatch}
                  disabled={processing || !batchId.trim()}
                  className={`w-full py-3 rounded-lg font-semibold transition-all text-base ${
                    processing || !batchId.trim()
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white shadow-lg hover:shadow-xl'
                  }`}
                >
                  {processing ? 'Processing...' : 'Associate Batch Proxies'}
                </button>
              </div>

              {batchResult && (
                <div className={`mt-3 p-3 rounded-lg border ${
                  batchResult.success 
                    ? 'bg-green-900/30 border-green-500/30' 
                    : 'bg-red-900/30 border-red-500/30'
                }`}>
                  <div className="flex items-start gap-2">
                    <svg 
                      className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                        batchResult.success ? 'text-green-400' : 'text-red-400'
                      }`} 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      {batchResult.success ? (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      ) : (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      )}
                    </svg>
                    <div>
                      <p className={`font-semibold text-xs ${
                        batchResult.success ? 'text-green-300' : 'text-red-300'
                      }`}>
                        {batchResult.success ? 'Success' : 'Error'}
                      </p>
                      <p className="text-gray-300 text-xs mt-0.5">{batchResult.message}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Quick Tips */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <h3 className="text-sm font-semibold text-white mb-3">Quick Tips</h3>
              <div className="space-y-2 text-xs text-gray-400">
                <div className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <span>Change the SESSION part to create unlimited unique proxies</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <span>Same SESSION = same IP (within duration)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <span>Max duration is 10080 minutes (7 days)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <span>No port limits - generate as many as you need!</span>
                </div>
              </div>
            </div>

            {/* Help */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <h3 className="text-sm font-semibold text-white mb-3">Need Help?</h3>
              <p className="text-xs text-gray-400 mb-3">
                Contact us on Telegram for support and integration help
              </p>
              <a
                href="https://t.me/NetloxProxies"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 rounded-lg text-white text-sm font-medium transition-all"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
                </svg>
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default APIPage;
